import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Flame, LogOut, Settings, MessageCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { KPIGrid } from '../components/dashboard/KPIGrid';
import { TelegramStatusCard, QuickActionsCard } from '../components/dashboard/StatusCards';
import { InterventionsTable } from '../components/dashboard/InterventionsTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [interventions, setInterventions] = useState([]);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  const scrollToList = () => {
    listRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, interventionsRes, telegramRes] = await Promise.all([
        axios.get(`${API_URL}/api/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/interventions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/telegram/config`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setInterventions(interventionsRes.data);
      setTelegramStatus(telegramRes.data);
    } catch {
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo intervento?')) return;
    try {
      await axios.delete(`${API_URL}/api/interventions/${id}`, { withCredentials: true });
      fetchData();
    } catch {
      setError('Errore eliminazione intervento');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#E3000F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="glass-header sticky top-0 z-50 border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Flame className="w-8 h-8 text-[#E3000F]" />
            <h1 className="text-xl font-bold uppercase tracking-tighter" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              VVF Interventi
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#A1A1AA] text-sm hidden md:block">{user?.name}</span>
            <Link to="/chat">
              <Button variant="ghost" size="icon" className="text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]" data-testid="chat-btn">
                <MessageCircle className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/settings">
              <Button variant="ghost" size="icon" className="text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]" data-testid="settings-btn">
                <Settings className="w-5 h-5" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {error && (
          <div className="bg-[#E3000F]/10 border border-[#E3000F]/30 p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-[#E3000F]" />
            <p className="text-[#E3000F]">{error}</p>
          </div>
        )}

        <KPIGrid stats={stats} onScrollToList={scrollToList} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <TelegramStatusCard telegramStatus={telegramStatus} />
          <QuickActionsCard onRefresh={fetchData} />
        </div>

        <div ref={listRef}>
          <InterventionsTable interventions={interventions} onDelete={handleDelete} />
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
